import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { RefineMusicOptions } from '../api/types';
import { isStudioUseMock, STUDIO_COPY } from '../config';
import {
  DEFAULT_MUSIC_FADE_IN_S,
  DEFAULT_MUSIC_FADE_OUT_S,
  DEFAULT_MUSIC_LOOP,
  DEFAULT_MUSIC_ONLY_GAIN_DB,
  type MusicMode,
  type MusicTrack,
} from './types';
import {
  defaultGameGainDb,
  defaultMusicGainDb,
  gainDbToPercent,
  percentToGainDb,
} from './loadTracks';
import { fetchMusicPreviewObjectUrl } from './previewAudio';

export type CustomizeMusicPanelProps = {
  tracks: MusicTrack[];
  musicId: string | null;
  musicMode: MusicMode;
  gameGainDb: number;
  musicGainDb: number;
  musicLoop?: boolean;
  musicFadeInS?: number;
  musicFadeOutS?: number;
  disabled?: boolean;
  /** V2 Music inspector: picker + loop/fade only; volumes live on Audio Mix. */
  hideGainSliders?: boolean;
  onChange: (next: RefineMusicOptions) => void;
};

const CustomizeMusicPanel: React.FC<CustomizeMusicPanelProps> = ({
  tracks,
  musicId,
  musicMode,
  gameGainDb,
  musicGainDb,
  musicLoop = DEFAULT_MUSIC_LOOP,
  musicFadeInS = DEFAULT_MUSIC_FADE_IN_S,
  musicFadeOutS = DEFAULT_MUSIC_FADE_OUT_S,
  disabled = false,
  hideGainSliders = false,
  onChange,
}) => {
  const selected = useMemo(
    () => tracks.find((t) => t.id === musicId) || null,
    [tracks, musicId]
  );
  const hasVocals = Boolean(selected?.has_vocals);
  const [auditionId, setAuditionId] = useState<string | null>(null);
  const auditionRef = useRef<HTMLAudioElement | null>(null);
  const fadesOn = musicFadeInS > 0 || musicFadeOutS > 0;

  const stopAudition = () => {
    const audio = auditionRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setAuditionId(null);
  };

  useEffect(() => {
    return () => {
      stopAudition();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount only
  }, []);

  // Stop audition when the selected mix track changes or panel disables.
  useEffect(() => {
    if (disabled) stopAudition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, musicId]);

  const emit = (patch: Partial<RefineMusicOptions>) => {
    onChange({
      music_id: musicId,
      music_mode: musicMode,
      game_gain_db: gameGainDb,
      music_gain_db: musicGainDb,
      music_loop: musicLoop,
      music_fade_in_s: musicFadeInS,
      music_fade_out_s: musicFadeOutS,
      ...patch,
    });
  };

  const onSelectTrack = (nextId: string) => {
    stopAudition();
    if (!nextId) {
      emit({
        music_id: null,
        music_mode: 'bed',
        game_gain_db: defaultGameGainDb(),
        music_gain_db: defaultMusicGainDb(),
        music_loop: DEFAULT_MUSIC_LOOP,
        music_fade_in_s: DEFAULT_MUSIC_FADE_IN_S,
        music_fade_out_s: DEFAULT_MUSIC_FADE_OUT_S,
      });
      return;
    }
    const track = tracks.find((t) => t.id === nextId);
    const vocals = Boolean(track?.has_vocals);
    const mode: MusicMode = vocals ? 'music_only' : 'bed';
    emit({
      music_id: nextId,
      music_mode: mode,
      music_gain_db: vocals
        ? DEFAULT_MUSIC_ONLY_GAIN_DB
        : musicGainDb === DEFAULT_MUSIC_ONLY_GAIN_DB
          ? defaultMusicGainDb()
          : musicGainDb,
    });
  };

  const toggleAudition = async (track: MusicTrack) => {
    if (disabled) return;
    if (auditionId === track.id) {
      stopAudition();
      return;
    }
    stopAudition();
    try {
      const url = await fetchMusicPreviewObjectUrl(track.id, {
        previewUrl: track.preview_url,
        useMock: isStudioUseMock(),
      });
      const audio = auditionRef.current;
      if (!audio) return;
      audio.src = url;
      audio.loop = true;
      audio.volume = 0.85;
      setAuditionId(track.id);
      await audio.play().catch(() => {
        // Autoplay / decode failures stay silent — keep picker usable.
        stopAudition();
      });
    } catch {
      // Flag lag / 403 / network: leave ▶ idle, no console noise.
      setAuditionId(null);
    }
  };

  const gamePct = gainDbToPercent(gameGainDb);
  const musicPct = gainDbToPercent(musicGainDb);
  const showModeToggle = Boolean(musicId) && !hasVocals;

  return (
    <div
      className="studio-music-panel"
      data-testid="studio-music-panel"
      id="studio-music-panel"
    >
      <audio ref={auditionRef} data-testid="studio-music-audition-audio" />
      <div className="studio-music-panel-header">
        <p className="studio-music-panel-title">{STUDIO_COPY.musicPickerTitle}</p>
        <p className="text-xs text-[var(--studio-muted)]">
          {STUDIO_COPY.musicRebuildNote}
        </p>
      </div>

      <div
        className="studio-music-track-list"
        role="listbox"
        aria-label={STUDIO_COPY.musicTrackLabel}
        data-testid="studio-music-select"
      >
        <div
          className={`studio-music-track-row ${!musicId ? 'is-selected' : ''}`}
          role="option"
          aria-selected={!musicId}
        >
          <span className="studio-music-audition-spacer" aria-hidden="true" />
          <button
            type="button"
            className="studio-music-track-pick"
            disabled={disabled}
            data-testid="studio-music-none"
            onClick={() => onSelectTrack('')}
          >
            {STUDIO_COPY.musicNone}
          </button>
        </div>
        {tracks.map((track) => {
          const isSelected = musicId === track.id;
          const isAuditioning = auditionId === track.id;
          return (
            <div
              key={track.id}
              className={`studio-music-track-row ${isSelected ? 'is-selected' : ''}`}
              role="option"
              aria-selected={isSelected}
            >
              <button
                type="button"
                className="studio-music-audition"
                disabled={disabled}
                aria-label={
                  isAuditioning
                    ? `Stop ${track.display_name}`
                    : `Audition ${track.display_name}`
                }
                data-testid={`studio-music-audition-${track.id}`}
                onClick={() => {
                  void toggleAudition(track);
                }}
              >
                {isAuditioning ? '⏹' : '▶'}
              </button>
              <button
                type="button"
                className="studio-music-track-pick"
                disabled={disabled}
                data-testid={`studio-music-pick-${track.id}`}
                onClick={() => onSelectTrack(track.id)}
              >
                {track.display_name}
                {track.has_vocals ? ` · ${STUDIO_COPY.musicVocalsTag}` : ''}
              </button>
            </div>
          );
        })}
      </div>

      {hasVocals ? (
        <p
          className="text-xs text-[var(--studio-accent-bright)]"
          data-testid="studio-music-vocal-hint"
        >
          {STUDIO_COPY.musicVocalHint}
        </p>
      ) : null}

      {showModeToggle ? (
        <div
          className="studio-music-mode"
          role="group"
          aria-label={STUDIO_COPY.musicModeLabel}
          data-testid="studio-music-mode"
        >
          <button
            type="button"
            className={musicMode === 'bed' ? 'is-active' : ''}
            disabled={disabled}
            data-testid="studio-music-mode-bed"
            onClick={() => emit({ music_mode: 'bed' })}
          >
            {STUDIO_COPY.musicModeBed}
          </button>
          <button
            type="button"
            className={musicMode === 'music_only' ? 'is-active' : ''}
            disabled={disabled}
            data-testid="studio-music-mode-only"
            onClick={() =>
              emit({
                music_mode: 'music_only',
                music_gain_db:
                  musicGainDb > DEFAULT_MUSIC_ONLY_GAIN_DB
                    ? DEFAULT_MUSIC_ONLY_GAIN_DB
                    : musicGainDb,
              })
            }
          >
            {STUDIO_COPY.musicModeOnly}
          </button>
        </div>
      ) : null}

      {musicId ? (
        <div className="studio-music-sliders" data-testid="studio-music-sliders">
          {!hideGainSliders ? (
            <>
          <label className="studio-music-field">
            <span className="studio-music-label">
              {STUDIO_COPY.musicGameVolume} · {gameGainDb.toFixed(0)} dB
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              disabled={disabled || musicMode === 'music_only'}
              value={Math.round(gamePct)}
              data-testid="studio-music-game-gain"
              onChange={(event) =>
                emit({ game_gain_db: percentToGainDb(Number(event.target.value)) })
              }
            />
          </label>
          <label className="studio-music-field">
            <span className="studio-music-label">
              {STUDIO_COPY.musicMusicVolume} · {musicGainDb.toFixed(0)} dB
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              disabled={disabled}
              value={Math.round(musicPct)}
              data-testid="studio-music-music-gain"
              onChange={(event) =>
                emit({
                  music_gain_db: percentToGainDb(Number(event.target.value)),
                })
              }
            />
          </label>
            </>
          ) : null}

          <label className="studio-music-toggle" data-testid="studio-music-loop">
            <input
              type="checkbox"
              checked={musicLoop}
              disabled={disabled}
              data-testid="studio-music-loop-toggle"
              onChange={(event) => emit({ music_loop: event.target.checked })}
            />
            <span>{STUDIO_COPY.musicLoopLabel}</span>
          </label>

          <label className="studio-music-toggle" data-testid="studio-music-fade">
            <input
              type="checkbox"
              checked={fadesOn}
              disabled={disabled}
              data-testid="studio-music-fade-toggle"
              onChange={(event) => {
                if (event.target.checked) {
                  emit({
                    music_fade_in_s: DEFAULT_MUSIC_FADE_IN_S,
                    music_fade_out_s: DEFAULT_MUSIC_FADE_OUT_S,
                  });
                } else {
                  emit({ music_fade_in_s: 0, music_fade_out_s: 0 });
                }
              }}
            />
            <span>{STUDIO_COPY.musicFadeLabel}</span>
          </label>

          {fadesOn ? (
            <div
              className="studio-music-fade-steps"
              data-testid="studio-music-fade-steps"
            >
              <label className="studio-music-field">
                <span className="studio-music-label">
                  {STUDIO_COPY.musicFadeInLabel}
                </span>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.25}
                  disabled={disabled}
                  value={musicFadeInS}
                  data-testid="studio-music-fade-in"
                  onChange={(event) =>
                    emit({
                      music_fade_in_s: Math.max(
                        0,
                        Number(event.target.value) || 0
                      ),
                    })
                  }
                />
              </label>
              <label className="studio-music-field">
                <span className="studio-music-label">
                  {STUDIO_COPY.musicFadeOutLabel}
                </span>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.25}
                  disabled={disabled}
                  value={musicFadeOutS}
                  data-testid="studio-music-fade-out"
                  onChange={(event) =>
                    emit({
                      music_fade_out_s: Math.max(
                        0,
                        Number(event.target.value) || 0
                      ),
                    })
                  }
                />
              </label>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CustomizeMusicPanel;
